import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Equipment API
export const equipmentAPI = {
  getAll: (params) => axios.get(`${API}/equipment`, { params }),
  getStats: () => axios.get(`${API}/equipment/stats`),
  getById: (id) => axios.get(`${API}/equipment/${id}`),
  create: (data) => axios.post(`${API}/equipment`, data),
  update: (id, data) => axios.put(`${API}/equipment/${id}`, data),
  delete: (id) => axios.delete(`${API}/equipment/${id}`),
  checkAvailability: (data) => axios.post(`${API}/equipment/check-availability`, data),
  checkAvailabilityBulk: (equipmentIds, startDate, endDate, excludeProjectId) => 
    axios.post(`${API}/equipment/check-availability-bulk`, equipmentIds, {
      params: { start_date: startDate, end_date: endDate, exclude_project_id: excludeProjectId }
    })
};

// Projects API
export const projectsAPI = {
  getAll: (params) => axios.get(`${API}/projects`, { params }),
  getStats: () => axios.get(`${API}/projects/stats`),
  getById: (id) => axios.get(`${API}/projects/${id}`),
  create: (data) => axios.post(`${API}/projects`, data),
  update: (id, data) => axios.put(`${API}/projects/${id}`, data),
  delete: (id) => axios.delete(`${API}/projects/${id}`)
};

// Customers API
export const customersAPI = {
  getAll: (params) => axios.get(`${API}/customers`, { params }),
  getStats: () => axios.get(`${API}/customers/stats`),
  getById: (id) => axios.get(`${API}/customers/${id}`),
  create: (data) => axios.post(`${API}/customers`, data),
  update: (id, data) => axios.put(`${API}/customers/${id}`, data),
  delete: (id) => axios.delete(`${API}/customers/${id}`)
};

// Crew API
export const crewAPI = {
  getAll: (params) => axios.get(`${API}/crew`, { params }),
  getStats: () => axios.get(`${API}/crew/stats`),
  getById: (id) => axios.get(`${API}/crew/${id}`),
  create: (data) => axios.post(`${API}/crew`, data),
  update: (id, data) => axios.put(`${API}/crew/${id}`, data),
  delete: (id) => axios.delete(`${API}/crew/${id}`)
};

// Invoices API
export const invoicesAPI = {
  getAll: (params) => axios.get(`${API}/invoices`, { params }),
  getStats: () => axios.get(`${API}/invoices/stats`),
  getById: (id) => axios.get(`${API}/invoices/${id}`),
  create: (data) => axios.post(`${API}/invoices`, data),
  update: (id, data) => axios.put(`${API}/invoices/${id}`, data),
  delete: (id) => axios.delete(`${API}/invoices/${id}`),
  recordPayment: (id, amount) => axios.post(`${API}/invoices/${id}/payment`, { payment_amount: amount })
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => axios.get(`${API}/dashboard/stats`)
};

// Calendar API
export const calendarAPI = {
  getEvents: (params) => axios.get(`${API}/calendar/events`, { params })
};

// Email API
export const emailAPI = {
  sendInvoice: (data) => axios.post(`${API}/email/send-invoice`, data),
  getSentInvoices: (limit = 10) => axios.get(`${API}/email/sent-invoices`, { params: { limit } })
};

// Equipment Calendar API
export const equipmentCalendarAPI = {
  getBookings: (startDate, endDate, equipmentId, category) => 
    axios.get(`${API}/equipment-calendar/bookings`, { 
      params: { start_date: startDate, end_date: endDate, equipment_id: equipmentId, category } 
    }),
  getDailySummary: (startDate, endDate, equipmentId, category) => 
    axios.get(`${API}/equipment-calendar/daily-summary`, { 
      params: { start_date: startDate, end_date: endDate, equipment_id: equipmentId, category } 
    })
};

// Auth API (for approval workflow)
export const authAPI = {
  getPendingUsers: () => axios.get(`${API}/auth/pending-users`),
  approveUser: (userId) => axios.post(`${API}/auth/approve/${userId}`),
  rejectUser: (userId) => axios.post(`${API}/auth/reject/${userId}`),
  getUsers: () => axios.get(`${API}/auth/users`),
  updateUser: (userId, data) => axios.put(`${API}/auth/users/${userId}`, data)
};

// Signature API
export const signatureAPI = {
  generateLink: (projectId, customerEmail) => axios.post(`${API}/signature/generate/${projectId}`, { customerEmail }),
  getQuoteForSigning: (token) => axios.get(`${API}/signature/quote/${token}`),
  signQuote: (token, signedBy, signatureData) => axios.post(`${API}/signature/sign/${token}`, { signedBy, signatureData }),
  getStatus: (projectId) => axios.get(`${API}/signature/status/${projectId}`)
};

// Crew Availability API
export const crewAvailabilityAPI = {
  checkAvailability: (data) => axios.post(`${API}/crew/check-availability`, data),
  checkAvailabilityBulk: (data) => axios.post(`${API}/crew/check-availability-bulk`, data)
};

// Crew Calendar API
export const crewCalendarAPI = {
  getBookings: (startDate, endDate, crewId, role) => 
    axios.get(`${API}/crew-calendar/bookings`, { 
      params: { start_date: startDate, end_date: endDate, crew_id: crewId, role } 
    }),
  getDailySummary: (startDate, endDate, crewId, role) => 
    axios.get(`${API}/crew-calendar/daily-summary`, { 
      params: { start_date: startDate, end_date: endDate, crew_id: crewId, role } 
    })
};

// Staffels API (Tiered Pricing)
export const staffelsAPI = {
  getAll: (params) => axios.get(`${API}/staffels`, { params }),
  getById: (id) => axios.get(`${API}/staffels/${id}`),
  create: (data) => axios.post(`${API}/staffels`, data),
  update: (id, data) => axios.put(`${API}/staffels/${id}`, data),
  delete: (id) => axios.delete(`${API}/staffels/${id}`),
  calculateDiscount: (type, days, quantity, basePrice) => 
    axios.post(`${API}/staffels/calculate-discount`, null, { 
      params: { type, days, quantity, base_price: basePrice } 
    })
};

// Company Settings API
export const companySettingsAPI = {
  get: () => axios.get(`${API}/settings/company`),
  update: (data) => axios.put(`${API}/settings/company`, data),
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/settings/company/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getLogo: () => axios.get(`${API}/settings/company/logo`),
  deleteLogo: () => axios.delete(`${API}/settings/company/logo`)
};

// Bookkeeping API
export const bookkeepingAPI = {
  // Transactions
  getTransactions: (params) => axios.get(`${API}/bookkeeping/transactions`, { params }),
  createTransaction: (data) => axios.post(`${API}/bookkeeping/transactions`, data),
  updateTransaction: (id, data) => axios.put(`${API}/bookkeeping/transactions/${id}`, data),
  deleteTransaction: (id) => axios.delete(`${API}/bookkeeping/transactions/${id}`),
  
  // Transaction Attachments
  uploadAttachment: (transactionId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/bookkeeping/transactions/${transactionId}/attachment`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteAttachment: (transactionId) => axios.delete(`${API}/bookkeeping/transactions/${transactionId}/attachment`),
  updateAttachmentStatus: (transactionId, status) => axios.put(`${API}/bookkeeping/transactions/${transactionId}/attachment-status`, null, { params: { status } }),
  
  // Reports
  getBtwDeclaration: (year, quarter) => axios.get(`${API}/bookkeeping/btw-declaration`, { params: { year, quarter } }),
  getProfitLoss: (year) => axios.get(`${API}/bookkeeping/profit-loss`, { params: { year } }),
  getBalance: (year) => axios.get(`${API}/bookkeeping/balance`, { params: { year } }),
  getSummary: (year) => axios.get(`${API}/bookkeeping/summary`, { params: { year } }),
  
  // Categories
  getCategories: (type) => axios.get(`${API}/bookkeeping/categories`, { params: { type } }),
  createCategory: (data) => axios.post(`${API}/bookkeeping/categories`, data),
  updateCategory: (id, data) => axios.put(`${API}/bookkeeping/categories/${id}`, data),
  deleteCategory: (id, force = false) => axios.delete(`${API}/bookkeeping/categories/${id}`, { params: { force } }),
  cleanupDuplicateCategories: () => axios.post(`${API}/bookkeeping/categories/cleanup-duplicates`)
};

// Banking API (CSV Import)
export const bankingAPI = {
  // Status
  getStatus: () => axios.get(`${API}/banking/status`),
  
  // Import history
  getImports: () => axios.get(`${API}/banking/imports`),
  deleteImport: (importId) => axios.delete(`${API}/banking/imports/${importId}`),
  
  // CSV Import
  importCSV: (file, bankType = 'auto') => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/banking/import`, formData, {
      params: { bank_type: bankType },
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// Rabobank Boekhoudkoppeling API
export const rabobankAPI = {
  // Status
  getStatus: () => axios.get(`${API}/rabobank/status`),
  
  // Authorization
  authorize: (redirectUri) => axios.get(`${API}/rabobank/authorize`, { params: { redirect_uri: redirectUri } }),
  callback: (code, state, redirectUri) => axios.get(`${API}/rabobank/callback`, { 
    params: { code, state, redirect_uri: redirectUri } 
  }),
  
  // Accounts & Transactions
  getAccounts: () => axios.get(`${API}/rabobank/accounts`),
  getTransactions: (dateFrom, dateTo) => axios.get(`${API}/rabobank/transactions`, {
    params: { date_from: dateFrom, date_to: dateTo }
  }),
  importTransactions: (dateFrom, dateTo) => axios.post(`${API}/rabobank/import`, null, {
    params: { date_from: dateFrom, date_to: dateTo }
  }),
  
  // Disconnect
  disconnect: () => axios.delete(`${API}/rabobank/disconnect`)
  };
  // Stripe Payments API
  export const paymentsAPI = {
    getStatus: () => axios.get(`${API}/payments/status`),
    createInvoiceCheckout: (invoiceId) => axios.post(`${API}/payments/invoice/${invoiceId}/checkout`),
    createDepositCheckout: (projectId) => axios.post(`${API}/payments/quote/${projectId}/deposit`),
    getPaymentStatus: (sessionId) => axios.get(`${API}/payments/status/${sessionId}`),
    getTransactions: () => axios.get(`${API}/payments/transactions`)
  };
